import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import WebFont from 'webfontloader';
import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';

import Header from '@/components/Header/Header';
import Loader, { Status } from '@/components/Loader/Loader';
import BusinessPreview from '@/modules/dashboard/components/BusinessPreview/BusinessPreview';
import {
	dashboardGetListsRequest,
	dashboardGetBusinessRequest,
	dashboardSelectBusiness,
	dashboardSelectList,
	dashboardSelectLocation,
	dashboardUpdateBusinessNotesRequest,
	dashboardRemoveModalBusiness,
	dashboardRemoveAllFromSaveList,
	dashboardClickDeleteNote,
	dashboardSetToStale
} from '@/modules/dashboard/dashboardActions';
import questionMarkCircle from '@/assets/images/question-mark-circle-png.png';
import type { Business, UserNotes } from '@/modules/dashboard/dashboardTypes';
import { reportAddRequest } from '@/modules/report/reportActions';
import { searchGetSearchesRequest } from '@/modules/search/searchActions';
import ResetPasswordModal from '@/modules/user/components/ResetPasswordModal';
import AddUserModal from '@/modules/user/components/AddUserModal';
import AddBccModal from '@/modules/user/components/AddBccModal';
import {
	logoutUserRequest,
	userGetUserRequest,
	userAddEmailToken,
	refreshTokenRequestAPI,
	userGetNewCommunications
} from '@/modules/user/userActions';
import {
	emailGetEmailStatuses,
	emailGetScheduledEmailLimits
} from '@/modules/email/emailActions';
import type { RootState } from '@/rootReducer';
import {
	getUserEmail,
	getUserName,
	getUserToken
} from '@/services/authentication';
import { sendAnalytics } from '@/services/analyticsService';
import Map from '@/utils/map';
import reportTimer from '@/utils/reportTimer';

import ListModal from '@/modules/dashboard/components/ListModal/ListModal';
import type { DirectEmail } from '@/modules/email/emailTypes';

import { productFruits } from 'product-fruits';

import './styles.scss';
import Admin from '@/modules/admin/scenes/Admin';

import {
	EmailSecondary,
	EmptyState,
	GoogleButtonIcon,
	MsButtonIcon,
	MarkerLocationIcon
} from '@/resources/assets/images';
import { startNylasUser } from '@/modules/emailOnboarding/emailOnboardingActions';
import StripeRedirect from '@/modules/freeTrial/scenes/StripeRedirect';
import Link from '@/components/Link/Link';
import { useNavBarContext } from '@/components/Header/NavBar/NavBarContext';
import TwoFactorAuthModal from '@/modules/user/components/TwoFactorAuthModal/TwoFactorAuthModal';
import Sequences from '@/modules/sequences copy/scenes/Sequences';

const Dashboard = React.lazy(
	() => import('@/modules/dashboard/scenes/Dashboard')
);
const Report = React.lazy(() => import('@/modules/report/scenes/Report'));
const DemandReports = React.lazy(
	() => import('@/modules/demandReports/scenes/DemandReports')
);
const UserLogin = React.lazy(() => import('@/modules/user/scenes/UserLogin'));

const CommunicationCenter = React.lazy(
	() => import('@/modules/communicationCenter/scenes/CommunicationCenter')
);
const Unsubscribe = React.lazy(
	() => import('@/modules/unsubscribe/scenes/Unsubscribe')
);
const EmailOnboarding = React.lazy(
	() => import('@/modules/emailOnboarding/scenes/EmailOnboarding')
);
const ScheduledEmailsPage = React.lazy(
	() => import('@/modules/scheduledEmailsPage/scenes/ScheduledEmailsPage')
);

WebFont.load({
	google: {
		families: [
			'Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900',
			'Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900'
		]
	}
});

const Main = (): JSX.Element => {
	const [
		isAuthorized,
		authorizeIsLoading,
		user,
		query,
		locations,
		locationsIsLoading,
		locationSelected,
		lists,
		listsIsLoading,
		listSelected,
		categoriesIsLoading,
		modalBusiness,
		requestRequired,
		logoutRequired
	] = useSelector(
		(state: RootState) => [
			state.user.get('isAuthorized'),
			state.user.getIn(['authorize', 'isLoading']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.get('query').toJS(),
			state.dashboard.getIn(['locations', 'data']).toJS(),
			state.dashboard.getIn(['locations', 'isLoading']),
			state.dashboard.getIn(['locations', 'selected']).toJS(),
			state.dashboard.getIn(['lists', 'data']).toJS(),
			state.dashboard.getIn(['lists', 'isLoading']),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.dashboard.getIn(['businesses', 'modalBusiness']),
			state.user.getIn(['authorize', 'user', 'refreshRequired']),
			state.user.getIn(['authorize', 'user', 'logoutRequired'])
		],
		shallowEqual
	);

	const dispatch = useDispatch();

	const refreshTokenAPI = (payload: any) =>
		dispatch(refreshTokenRequestAPI(payload));

	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());
	const getUser = () => dispatch(userGetUserRequest());
	const addUserEmailToken = (email, code, stateToken) =>
		dispatch(userAddEmailToken({ userEmail: email, code, stateToken }));
	const getBusiness = (payload: any) =>
		dispatch(dashboardGetBusinessRequest(payload));
	const setBusinessNotes = (businessId: string, notes: UserNotes) =>
		dispatch(dashboardUpdateBusinessNotesRequest({ businessId, notes }));
	const logoutUser = () => dispatch(logoutUserRequest());
	const reportBusinesses = () => dispatch(reportAddRequest());
	const getLists = () => dispatch(dashboardGetListsRequest());
	const getSearches = () => dispatch(searchGetSearchesRequest());
	const selectBusiness = (b: Partial<Business>) =>
		dispatch(dashboardSelectBusiness(b as Business));
	const selectList = (id: string) => dispatch(dashboardSelectList(id));
	const selectLocation = (id: string) =>
		dispatch(dashboardSelectLocation(id));
	const removeModalBusiness = () => dispatch(dashboardRemoveModalBusiness());
	const startEmailSignup = payload => dispatch(startNylasUser(payload));
	const getEmailStatuses = () => dispatch(emailGetEmailStatuses());
	const clickDeleteNote = () => dispatch(dashboardClickDeleteNote());
	const getScheduledEmailLimits = () =>
		dispatch(emailGetScheduledEmailLimits());

	useEffect(() => {
		if (
			user.nylasSyncStatus === 'running' ||
			user.nylasSyncStatus === 'nothing' ||
			user.nylasSyncStatus === 'partial' ||
			user.nylasSyncStatus === undefined
		) {
			setNylasSyncStatus(true);
		} else {
			setNylasSyncStatus(false);
		}
	}, [user && user.nylasSyncStatus]);

	const { setHovered } = useNavBarContext();

	const [searchFocused, setSearchFocused] = useState<boolean>(false);
	const [showPasswordReset, setShowPasswordReset] = useState(false);
	const [show2faModal, setShow2faModal] = useState(false);
	const [businessModalTriggered, setBusinessModalTriggered] = useState(false);
	const [saveToList, setSaveToList] = useState<Business[]>();
	const history = useHistory();
	const isVisible = usePageVisibility();
	const parentNodeName = 'main';
	const { search, pathname } = useLocation();
	const businessId = new URLSearchParams(search).get('businessId');
	const linkType = new URLSearchParams(search).get('type');
	const [directEmail, setDirectEmail] = useState<DirectEmail>();
	const [nylasSynced, setNylasSyncStatus] = useState(
		() =>
			user?.nylasSyncStatus == 'running' ||
			user?.nylasSyncStatus == 'nothing' ||
			user.nylasSyncStatus == 'partial'
	);
	const [adminToken, setAdminToken] = useState<string>('');
	const [deleteDataIndex, setDeleteDataIndex] = useState<number | null>(null);
	const [sendAllMode, setSendAllMode] = useState<boolean>(false);

	useLayoutEffect(() => {
		if (window.opener) {
			if (
				pathname.toLowerCase().startsWith('/adcampaign/callback/error')
			) {
				window.opener.postMessage({ fbOauthError: true }, '*');
				window.close();
			}
		}
	}, [pathname]);

	useEffect(() => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User landed on page',
				properties: {
					href: window.location.href
				}
			});

			if (
				window.location.href.includes('namespace') &&
				user.threadedEmail
			) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: 'User successfully joined nylas',
					properties: {
						href: window.location.href
					}
				});
				setNylasSyncStatus(true);
			}

			const nylasUrlCode = new URLSearchParams(search).get('code');

			if (nylasUrlCode) {
				/// / The return does not have a stateToken. I left it in so that we wouldn't need
				/// / to make any other modifications, let me know what else is needed
				addUserEmailToken(user.email, nylasUrlCode, {
					stateToken: undefined
				});
			}
		}
	}, [user && user.email]);

	const setBusinessNotesDispatch = (bizId: string, notes: UserNotes) => {
		setBusinessNotes(businessId as string, notes);
	};

	const getNewCommunications = () => {
		dispatch(userGetNewCommunications());
	};

	const setStale = () => {
		dispatch(dashboardSetToStale());
	};

	const locationChange = (location: string) => {
		if (Map.get()) {
			Map.remove();
		}

		setStale();

		history.push('/');

		selectList('');

		selectBusiness({});

		removeAllFromSaveList();

		selectLocation(location);
	};

	const openFilter = e => {
		if (e && e.classList.contains('input-open')) {
			e.classList.remove('input-open');
		} else {
			e.classList.add('input-open');
		}
	};

	const listChange = (list: string) => {
		if (Map.get()) {
			Map.remove();
		}

		setStale();

		history.push('/');

		selectLocation('');

		selectBusiness({});

		removeAllFromSaveList();

		selectList(list);
	};

	useEffect(() => {
		if (user && user.email) {
			sendAnalytics({
				type: 'identify',
				userId: user.email,
				traits: {
					name: user.name,
					email: user.email,
					Organization:
						user.company && user.company.name
							? user.company.name
							: '',
					nylasUser: !!user?.threadedEmail?.nylasEnabled,
					Role: user?.role,
					CompanyId: user.companyId,
					TestGroup: user?.features?.businessSortingTestVariant
				}
			});
		}

		if (
			user.nylasSyncStatus === 'running' ||
			user.nylasSyncStatus === 'nothing' ||
			user.nylasSyncStatus === 'partial'
		) {
			setNylasSyncStatus(true);
		}
	}, [user.email, user.companyId, user.name, user.features]);

	useEffect(() => {
		if (
			listSelected &&
			Object.keys(listSelected).length > 0 &&
			user &&
			user.email
		) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User selected a list',
				properties: {
					listId: listSelected.id,
					listName: listSelected.name,
					listCreated: listSelected.createdAt,
					listLastUpdated: listSelected.updatedAt,
					listBusiness:
						listSelected.businessIds.length < 1000
							? listSelected.businessIds
							: 'too large to add'
				}
			});
		}
	}, [listSelected.id]);

	useEffect(() => {
		if (
			locationSelected &&
			Object.keys(locationSelected).length > 0 &&
			user &&
			user.email
		) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User selected a location',
				properties: {
					locationId: locationSelected.id,
					locationName: locationSelected.name,
					locationCity: locationSelected.city,
					locationState: locationSelected.state,
					locationStreet: locationSelected.street,
					locationZip: locationSelected.zip
				}
			});
		}
	}, [locationSelected.id]);

	useEffect(() => {
		if (!isVisible) {
			reportBusinesses();
		}
	}, [isVisible]);

	useEffect(() => {
		if (
			(getUserToken() || isAuthorized) &&
			process.env.REACT_SHOULD_REPORT
		) {
			reportTimer(reportBusinesses);
		}
	}, [isAuthorized]);

	useEffect(() => {
		if (requestRequired) {
			refreshTokenAPI({ reload: true });
		}
	}, [requestRequired]);

	useEffect(() => {
		if (logoutRequired && logoutRequired === true) {
			logoutUser();
		}
	}, [logoutRequired]);

	useEffect(() => {
		if (user && user.emailLimitLeft && user.emailLimitLeft % 5 === 0) {
			refreshTokenAPI({ reload: false });
		}
	}, [user.emailLimitLeft]);

	const refreshTokenTimeout = () => {
		setTimeout(() => {
			refreshTokenAPI({ reload: false, resetTokenTimeout: true });
		}, 1800000);
	};

	useEffect(() => {
		const token = localStorage.getItem('user_token');
		if (token && (!getUserToken() || !getUserName() || !getUserEmail())) {
			logoutUser();
		} else {
			refreshTokenTimeout();
		}
	}, []);

	const freemiumAuthorization = () => {
		if (user && user?.facebookFreemium) {
			return true;
		}
		return false;
	};

	useEffect(() => {
		if (user && user.email) {
			if (!freemiumAuthorization()) {
				getEmailStatuses();

				getLists();

				getSearches();

				getSearches();

				getNewCommunications();

				getScheduledEmailLimits();

				getNewCommunications();
			}
		}
	}, [user.email]);

	useEffect(() => {
		if (
			isAuthorized ||
			(getUserToken() && getUserName() && getUserEmail())
		) {
			getUser();
			startProductFruits();
		}
	}, [isAuthorized, adminToken]);

	const startProductFruits = () => {
		if (!user?.email) return;
		const userInfo = {
			username: user.email,
			email: user.email,
			firstname: user.name,
			role: user.role
		};
		if (
			!window?.location?.href?.includes('?admin=') ||
			userInfo.username != undefined
		) {
			productFruits.init('bHU0rkthiLgiic1L', 'en', userInfo, {
				disableLocationChangeDetection: true
			});
		}
	};

	const closeReauthenticate = () => {
		setNylasSyncStatus(true);
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User closed Nylas sync warning`
			});
		}
	};

	const openSingleBusinessModal = () => {
		const modalEl = document.getElementsByClassName(
			'single-business-modal'
		)[0];
		if (modalEl) {
			modalEl.classList.add('high-z');
			setTimeout(() => {
				modalEl.classList.add('visible');
			});
		}
	};

	const closeSingleBusinessModal = () => {
		const modalEl = document.getElementsByClassName(
			'single-business-modal'
		)[0];
		if (modalEl) {
			setDirectEmail(undefined);
			removeModalBusiness();
			modalEl.classList.remove('visible');
			setTimeout(() => {
				modalEl.classList.remove('high-z');
			}, 500);
		}
	};

	const addDeepLinkAnalytic = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User landed on a deep-linked business from: ${linkType}`,
				properties: {
					business: modalBusiness
				}
			});
		}
	};

	useEffect(() => {
		if (
			modalBusiness &&
			Object.keys(modalBusiness).length > 0 &&
			user &&
			user.email
		) {
			addDeepLinkAnalytic();
		}
	}, [modalBusiness]);

	const loadSingleBusinessModal = async bizId => {
		setBusinessModalTriggered(true);
		await getBusiness({ businessId: bizId, userEmail: user.email });
		setTimeout(() => {
			openSingleBusinessModal();
		}, 1500);
	};

	const saveBizToList = (b: any) => {
		setSaveToList(b);
	};

	const closeModal = () => {
		const e = document.getElementById('email-verification-modal');
		e.classList.add('email-verification-modal-fade');
		setTimeout(() => {
			e.classList.add('email-verification-modal-closed');
		}, 500);
	};

	const openModal = () => {
		const e = document.getElementById('email-verification-modal');

		e?.classList.remove('email-verification-modal-fade');
		setTimeout(() => {
			e?.classList.remove('email-verification-modal-closed');
		}, 500);
	};

	const openDeleteNoteModal = () => {
		const e = document.getElementById('delete-note-modal');
		e.classList.remove('delete-modal-closed');
		setTimeout(() => e.classList.remove('delete-modal-fade'), 0);
	};

	const closeDeleteNoteModal = () => {
		const e = document.getElementById('delete-note-modal');
		e.classList.add('delete-modal-fade');
		setTimeout(() => {
			e.classList.add('delete-modal-closed');
		}, 500);
	};

	useEffect(() => {
		if (
			locationsIsLoading ||
			listsIsLoading ||
			categoriesIsLoading ||
			authorizeIsLoading
		) {
			return;
		}

		const dismissCount = Number(localStorage.getItem('dismissCount')) || 0;

		if (dismissCount >= 3) {
			return;
		}

		const modalDismissedTimestamp = Number(
			localStorage.getItem('modalDismissedTimestamp')
		);

		if (modalDismissedTimestamp) {
			const now = dayjs().valueOf();
			const hoursElapsed =
				Math.abs(now - modalDismissedTimestamp) / 1000 / 60 / 60;

			if (hoursElapsed < 24) {
			}
		}
	}, [
		locationsIsLoading,
		listsIsLoading,
		categoriesIsLoading,
		authorizeIsLoading
	]);

	useEffect(() => {
		const onCalAuthPage = window.location.pathname === '/calendarauth';
		if (onCalAuthPage) {
			return;
		}
		if (
			(user?.emailTokenValid ||
				user?.emailTokenInvalid ||
				user?.emailTokenExpired) &&
			!locationsIsLoading &&
			!listsIsLoading &&
			!categoriesIsLoading &&
			!authorizeIsLoading
		) {
			openModal();
		}
	}, [
		user?.emailTokenValid,
		user?.emailTokenInvalid,
		user?.emailTokenExpired,
		locationsIsLoading,
		listsIsLoading,
		categoriesIsLoading,
		authorizeIsLoading
	]);

	const openTemplateWarning = () => {
		const modalEl = document.getElementsByClassName(
			'template-warning-outer'
		)[0];
		if (modalEl) {
			modalEl.classList.add('template-warning-outer-exists');
			setTimeout(() => {
				modalEl.classList.add('template-warning-outer-visible');
			}, 50);
		}
	};

	const closeTemplateWarning = () => {
		const modalEl = document.getElementsByClassName(
			'template-warning-outer'
		)[0];
		if (modalEl) {
			modalEl.classList.remove('template-warning-outer-visible');
			setTimeout(() => {
				modalEl.classList.remove('template-warning-outer-exists');
				localStorage['templateCountLocal'] = 0;
			}, 400);
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User closed template warning modal'
			});
		}
	};

	useEffect(() => {
		if (localStorage['templateCountLocal'] >= 50) {
			openTemplateWarning();
		}
	}, [localStorage['templateCountLocal']]);

	const emailVerificationModalText = () => {
		if (user?.emailTokenValid) {
			return (
				<div className="email-verification-modal-title">
					<div>Email Verification</div>
					<p>
						Your email verification is currently processing. Please
						allow a few moments for this to be complete!
					</p>
					<p>
						Once processing is complete, you'll be able to email
						businesses, receive replies from businesses and see
						email history on the Resquared platform.
					</p>
				</div>
			);
		}

		if (user?.emailTokenExpired) {
			return (
				<div className="email-verification-modal-title">
					Oh no! Your state token expired while trying to connect your
					email account &nbsp;
					<p>
						Don't worry, simply try again and make sure to finish
						the process before navigating away from the window.
					</p>
				</div>
			);
		}

		return (
			<div className="email-verification-modal-title">
				Oh no! Your state token was invalid while trying to connect your
				email account &nbsp;
				<p>
					Don't worry, simply try again and make sure to finish the
					process before navigating away from the window.
				</p>
			</div>
		);
	};

	const emailVerificationPrimaryClickHandler = () => {
		closeModal();
		window.location.reload();
	};

	const emailVerificationButtons = () => {
		if (user?.emailTokenValid) {
			return (
				<Link redirect to="/">
					<button
						className="email-verification-primary"
						onClick={emailVerificationPrimaryClickHandler}
					>
						<p>Got it</p>
					</button>
				</Link>
			);
		}

		return (
			<>
				<button
					className="email-verification-primary"
					onClick={() => (setNylasSyncStatus(false), closeModal())}
				>
					<p>Try again</p>
				</button>
			</>
		);
	};

	if (pathname === '/stripe-redirect') {
		return <StripeRedirect />;
	}

	if (pathname === '/unsubscribe') {
		return (
			<Suspense
				fallback={
					<div className="flex h-screen w-screen items-center justify-center">
						<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
							<Loader status={Status.LOADING} />
						</div>
					</div>
				}
			>
				<Unsubscribe />
			</Suspense>
		);
	}
	if (
		window.location.pathname != '/fb-ads-signup' &&
		(getUserToken() || isAuthorized)
	) {
		if (
			locationsIsLoading ||
			listsIsLoading ||
			categoriesIsLoading ||
			authorizeIsLoading
		) {
			return (
				<div className="flex h-screen w-screen items-center justify-center">
					<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
						<Loader status={Status.LOADING} />
					</div>
				</div>
			);
		}


		if (
			businessId &&
			!modalBusiness &&
			!businessModalTriggered &&
			user &&
			user.email
		) {
			loadSingleBusinessModal(businessId);
		}
		return (
			<div className="flex h-screen w-screen flex-col">
				<div className="template-warning-outer">
					<div className="template-warning-background" />
					<div className="template-warning-container">
						<span className="template-warning-content">
							We recommend&nbsp;
							<span>changing your template&nbsp;</span>
							every 50 emails to avoid being flagged as spam.
							<img
								src={questionMarkCircle}
								className="question-mark-icon"
							/>
							<div className="template-warning-hovertext-container">
								<div className="arrow-up" />
								Spam filters may notice when you send the same
								message over and over again. To avoid being
								marked as a spammer by your or your recipients'
								email server, we recommend changing your email
								template every ~50 emails.
								<br />
								<br />
								<strong>
									We'll let you know when you've used the same
									template 50 times in a row so you can go
									back and select a different template.
								</strong>
								<br />
								<br />
								For even better deliverability, tailor messaging
								specifically to each business that you're
								reaching out to!
							</div>
						</span>
						<div
							className="template-warning-close-button"
							onClick={() => {
								localStorage['templateCountLocal'] -= 1;
								closeTemplateWarning();
							}}
						>
							Got it
						</div>
					</div>
				</div>
				{modalBusiness && modalBusiness.name ? (
					<div className="single-business-modal">
						<ListModal
							businesses={saveToList}
							testBiz={[modalBusiness]}
							saveBusinessToList={saveBizToList}
							lists={lists}
							listChange={listChange}
						/>
						<div
							role="button"
							aria-label="Close Business Modal"
							className="single-business-modal-bg"
							onClick={closeSingleBusinessModal}
							onKeyUp={closeSingleBusinessModal}
							tabIndex={0}
						/>
						<div className="single-business-modal-inner">
							<BusinessPreview
								className="slide-full-enter-done"
								saveNote={setBusinessNotesDispatch}
								business={modalBusiness}
								parentNodeName={parentNodeName}
								close={closeSingleBusinessModal}
								saveBusinessToList={setSaveToList}
								onEmail={email => {
									const newEmail = { ...email };
									newEmail.business._id =
										newEmail.business.id;

									setDirectEmail(newEmail);
								}}
								openDeleteNoteModal={openDeleteNoteModal}
								deleteDataIndex={deleteDataIndex}
								setDeleteDataIndex={setDeleteDataIndex}
							/>
						</div>
					</div>
				) : (
					''
				)}

				<ResetPasswordModal
					onClose={() => setShowPasswordReset(false)}
					show={showPasswordReset}
				/>

				{show2faModal && (
					<TwoFactorAuthModal setShow2faModal={setShow2faModal} />
				)}

				<AddUserModal />

				{!nylasSynced &&
					!query?.coordinates &&
					!query?.listId &&
					pathname === '/' && (
						<div className="overlay-header-nylas-authenticate" />
					)}

				<AddBccModal />

				<div className="main-header flex w-full flex-row justify-between px-4 md:px-2 lg:px-3">
					<Header
						locations={locations}
						locationSelected={locationSelected}
						locationChange={locationChange}
						lists={lists}
						listSelected={listSelected}
						listChange={listChange}
						onPasswordReset={() => setShowPasswordReset(true)}
						isOnboarded={user.isOnboarded}
						shouldRide={
							!locationSelected.location &&
							!listSelected.businessIds &&
							(pathname === '/' || pathname === '/login') &&
							!showPasswordReset
						}
						user={user}
						setNylasSyncStatus={setNylasSyncStatus}
						setShow2faModal={setShow2faModal}
					/>
				</div>
				<div className="relative flex w-full flex-1 flex-col overflow-hidden">
					{!query?.coordinates &&
						!query?.listId &&
						pathname === '/' && (
							<div className="semi-transparent-header w-100 h-100 dashboard-container--overlay pointer-events-auto absolute overflow-hidden" />
						)}
					<div
						className={`${
							pathname !== '/communication-center'
								? 'relative flex w-full flex-1 flex-row overflow-hidden'
								: ''
						}`}
					>
						<Switch>
							<Route path="/health">
								<h3>Hey There!!! The App is Healthy</h3>
							</Route>
							<Route path="/ready">
								<h3>Hey There!!! The App is Ready</h3>
							</Route>
							<Route path="/reports">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<Report />
								</Suspense>
							</Route>
							<Route path="/demand-reports">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<DemandReports />
								</Suspense>
							</Route>
							<Route path="/scheduled-emails">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<ScheduledEmailsPage />
								</Suspense>
							</Route>
							<Route path="/communication-center">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<CommunicationCenter
										openDeleteNoteModal={
											openDeleteNoteModal
										}
										deleteDataIndex={deleteDataIndex}
										setDeleteDataIndex={setDeleteDataIndex}
										listChange={listChange}
									/>
								</Suspense>
							</Route>
							<Route path="/admin">
								<Admin />
							</Route>
							<Route path="/adcampaign">
							</Route>
							<Route path="/unsubscribe">
								<Unsubscribe />
							</Route>
							<Route path="/email-onboarding">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<EmailOnboarding />
								</Suspense>
							</Route>
							<Route path="/verify-email-onboarding">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<EmailOnboarding />
								</Suspense>
							</Route>
							<Route path="/email-calendar-onboarding">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<EmailOnboarding />
								</Suspense>
							</Route>
							<Route path="/sequences">
								<Sequences />
							</Route>
							<Route path="/">
								<Suspense
									fallback={
										<div className="flex h-screen w-screen items-center justify-center">
											<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
												<Loader
													status={Status.LOADING}
												/>
											</div>
										</div>
									}
								>
									<Dashboard
										locationChange={locationChange}
										listChange={listChange}
										mainDirectEmail={directEmail}
										openDeleteNoteModal={
											openDeleteNoteModal
										}
										deleteDataIndex={deleteDataIndex}
										setDeleteDataIndex={setDeleteDataIndex}
										sendAllMode={sendAllMode}
										setSendAllMode={setSendAllMode}
									/>
									{!query?.coordinates &&
										!query?.listId &&
										pathname === '/' &&
										!nylasSynced && (
											<div className="main-reauthenticate">
												<div className="reauthenticate-holder">
													<div className="reauthenticate-header">
														We need to
														re-authenticate your
														Email to access
														communication features
													</div>
													<div className="reauthenticate-text">
														This can be caused by
														email account password
														or other company email
														settings being changed.
														If you recently verified
														your email, please wait
														10 minutes before
														re-verifying
													</div>
													<div
														className="signup-button"
														onClick={() => {
															startEmailSignup({
																user
															});
														}}
														onKeyDown={() => {
															startEmailSignup({
																user
															});
														}}
													>
														<GoogleButtonIcon className="email-logo email-logo-a" />
														<MsButtonIcon className="email-logo email-logo-b" />
														<EmailSecondary className="email-logo email-logo-c" />
														<span className="button-text">
															Verify Email
														</span>
													</div>
													<span
														onClick={() =>
															closeReauthenticate()
														}
														className="reauthenticate-remind-me-later"
													>
														Remind me later
													</span>
												</div>
											</div>
										)}
									{!query?.coordinates &&
										!query?.listId &&
										pathname === '/' &&
										(nylasSynced == true ||
											nylasSynced == undefined) && (
											<>
												<div className="main-empty-background" />
												<div className="main-empty-state">
													<div className="empty-state-image">
														<EmptyState className="empty-state-image-file" />
													</div>
													<div className="empty-state-header">
														Select a Location or a
														List to get started
													</div>
													<div
														className="empty-state-button-holder"
														onClick={e => {
															setSearchFocused(
																false
															);
															const tar =
																document.getElementById(
																	'header-input-location-element'
																);
															setTimeout(() => {
																openFilter(tar);
															}, 0);
															setHovered(
																'Locations'
															);
														}}
													>
														<MarkerLocationIcon className="empty-state-location-icon" />
														<div className="empty-state-text">
															Select a Location
														</div>
													</div>
												</div>
											</>
										)}
								</Suspense>
							</Route>
						</Switch>
					</div>
				</div>
				<div
					className="email-verification-modal email-verification-modal-closed email-verification-modal-fade"
					id="email-verification-modal"
				>
					<div
						className="email-verification-modal-background"
						onClick={closeModal}
					/>
					<div className="email-verification-inner-modal">
						{emailVerificationModalText()}
						<span className="email-verification-button-holder">
							{emailVerificationButtons()}
						</span>
					</div>
				</div>

				<div
					className="delete-modal delete-modal-closed delete-modal-fade"
					id="delete-note-modal"
				>
					<div
						className="delete-modal-background"
						onClick={closeDeleteNoteModal}
					/>
					<div className="delete-inner-modal">
						<div className="delete-modal-title">
							Are you sure you want to discard this note? &nbsp;
							<p>This note will be permanently deleted.</p>
						</div>
						<div className="delete-button-holder">
							<button
								className="cancel-delete"
								onClick={closeDeleteNoteModal}
							>
								Cancel
							</button>
							<button
								className="delete-permanently"
								onClick={() => {
									clickDeleteNote();
									closeDeleteNoteModal();
								}}
							>
								<p>Delete permanently</p>
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<Suspense
				fallback={
					<div className="flex h-screen w-screen items-center justify-center">
						<div className="flex w-1/5 max-w-sm flex-col items-center justify-center">
							<Loader status={Status.LOADING} />
						</div>
					</div>
				}
			>
				<UserLogin />
			</Suspense>
		</>
	);
};

export default Main;
